import axios from "axios";
import { useErrorStore } from "../store/errorStore";
import { trackApiError } from "../analytics/analyticsUtil";
import { getFirebaseToken, signOutUser } from "./authService";

const API_BASE_URL = process.env.EXPO_PUBLIC_CORE_SERVICE_BASE_URL;
const MAX_RETRIES = 3;
const RETRY_DELAY = 100; // ms

const RETRY_STATUS = [408, 429, 500, 502, 503, 504];

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

/* ------------------------------------------------------------------ */
/* TOKEN REFRESH STATE */
/* ------------------------------------------------------------------ */

let refreshPromise: Promise<string | null> | null = null;

/* ------------------------------------------------------------------ */
/* REQUEST INTERCEPTOR */
/* ------------------------------------------------------------------ */

apiClient.interceptors.request.use(
  async (config) => {
    try {
      const token = await getFirebaseToken();

      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    } catch (error) {
      useErrorStore.setState({ error });
      trackApiError(error, "axios.request", config?.url);
      return Promise.reject(error);
    }
  }
);

/* ------------------------------------------------------------------ */
/* RESPONSE INTERCEPTOR */
/* ------------------------------------------------------------------ */
apiClient.interceptors.response.use(
  (response) => response,

  async (error) => {
    const originalRequest = error.config;

    if (!originalRequest) {
      return Promise.reject(error);
    }

    originalRequest._retryCount = originalRequest._retryCount || 0;

    /* --------------------------------------------------------------- */
    /* NETWORK FAILURE / TIMEOUT DETECTION */
    /* --------------------------------------------------------------- */

    const isNetworkError = !error.response;
    const isTimeout = error.code === "ECONNABORTED";

    /* --------------------------------------------------------------- */
    /* RETRY LOGIC */
    /* --------------------------------------------------------------- */

    const status = error.response?.status;

    const shouldRetry =
      originalRequest._retryCount < MAX_RETRIES &&
      (isNetworkError ||
        isTimeout ||
        (status && RETRY_STATUS.includes(status)));

    if (shouldRetry) {
      originalRequest._retryCount += 1;

      await sleep(RETRY_DELAY * originalRequest._retryCount);

      return apiClient(originalRequest);
    }

    /* --------------------------------------------------------------- */
    /* HANDLE 401 */
    /* --------------------------------------------------------------- */

    if (status === 401 && !originalRequest._retry401) {
      originalRequest._retry401 = true;
      await sleep(RETRY_DELAY); // Small delay before token refresh
      try {
        if (!refreshPromise) {
          refreshPromise = (async () => {
            try {
              const token = await getFirebaseToken(true);
              return token;
            } finally {
              refreshPromise = null;
            }
          })();
        }

        const newToken = await refreshPromise;

        if (!newToken) {
          await signOutUser();
          return Promise.reject(error);
        }

        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;

        return apiClient(originalRequest);

      } catch (refreshError) {
        await signOutUser();

        useErrorStore.setState({ error: refreshError });
        trackApiError(refreshError, "axios.401.logout", originalRequest?.url);

        return Promise.reject(refreshError);
      }
    }

    /* --------------------------------------------------------------- */
    /* FINAL ERROR (AFTER RETRIES) */
    /* --------------------------------------------------------------- */

    useErrorStore.setState({ error });
    trackApiError(error, "axios.response", originalRequest?.url);

    return Promise.reject(error);
  }
);

export default apiClient;